// @flow
import {fromPairs, intersection, toPairs} from 'ramda';
import {createSelector} from 'reselect';

import {
    DEFAULT_PAYMENT_METHODS,
    PAYMENT_METHODS_INSTANT,
    PAYMENT_METHODS_UNAVAILABILITY,
} from 'constants/checkout/paymentMethods';
import {type PaymentsType} from 'constants/flow/flowTypesCart';
import {
    type ApplicationStoreType,
    type PaymentOsSavedCardType,
    type PaymentRequestOptionsType,
    type PaymentType,
} from 'constants/flow/flowTypes';

import {isProperty} from 'libs/utils/utils';
import {getActivePaymentMethod, paymentMethodCode} from 'selectors/checkout/checkout';
import {getCurrentPage} from 'selectors/app/app';
import {type PageAppType} from 'reducers/app/pageApp/pageAppType';

type PaymentMethodsType = {[string]: PaymentType};

/**
 * Get payment buttons for cart and checkout pages
 * @param {ApplicationStoreType} store - Application store
 * @returns {PaymentsType} - Payment buttons data
 */
export const getPayments = (store: ApplicationStoreType): PaymentsType => store.payments;

/**
 * Get all available payment methods
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {PaymentMethodsType} - payment methods
 */
export const getPaymentMethods = (store: ApplicationStoreType): PaymentMethodsType =>
    fromPairs(
        toPairs(store.paymentMethods.list).filter(
            ([paymentCode]: Array<string | PaymentType>) =>
                !(
                    PAYMENT_METHODS_UNAVAILABILITY[paymentCode] &&
                    PAYMENT_METHODS_UNAVAILABILITY[paymentCode]()
                ),
        ),
    );

/**
 * Get sorted payment methods
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {Array<string>} - sorted payment keys
 */
export const getSortedPaymentMethods = createSelector(
    getPaymentMethods,
    (paymentMethods: PaymentMethodsType): Array<string> =>
        Object.keys(paymentMethods).sort((firstElement: string, secondElement: string): number =>
            paymentMethods[firstElement].order < paymentMethods[secondElement].order ? -1 : 1,
        ),
);

/**
 * Get payment method to redirect to
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {PaymentType | null} - payment method to redirect
 */
export const getPaymentMethodToRedirect = createSelector(
    getPaymentMethods,
    (paymentMethods: PaymentMethodsType): PaymentType | null => {
        const defaultMethods = intersection(DEFAULT_PAYMENT_METHODS, Object.keys(paymentMethods));
        return defaultMethods[0] ? paymentMethods[defaultMethods[0]] : null;
    },
);

/**
 * Get active payment method save order errors
 */
export const getPaymentMethodOrderErrors = createSelector(
    getActivePaymentMethod,
    paymentMethod => paymentMethod.orderErrors,
);

/**
 * Get is payment method available for instant purchase
 * @returns {boolean} - is payment instant
 */
export const getIsPaymentInstant = createSelector(paymentMethodCode, code =>
    PAYMENT_METHODS_INSTANT.includes(code),
);

/**
 * Returns card details by passed payment method token
 * @param {PaymentType} activePaymentMethod - active payment method
 * @param {string} token- token to look for
 * @returns {PaymentOsSavedCardType | typeof undefined} Saved card by token
 */
export const getSavedCardByToken = createSelector(
    getActivePaymentMethod,
    (_, token) => token,
    (
        {cards}: PaymentType,
        token: string,
        // eslint-disable-next-line no-undefined
    ): PaymentOsSavedCardType | typeof undefined => {
        return cards.find(({token: cardToken}) => cardToken === token);
    },
);

/**
 * Check payments status
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is loaded
 */
export const getPaymentsFulfilled = isProperty('payments.status', 'fulfilled');

/**
 * Payment request options
 * @returns {PaymentRequestOptionsType} payment request options object
 */
export const getPaymentRequestOptions = createSelector(
    getCurrentPage,
    ({page}: PageAppType): PaymentRequestOptionsType => {
        const isNonCheckout = page !== 'checkout';
        return {
            requestPayerName: isNonCheckout,
            requestPayerEmail: isNonCheckout,
            requestPayerPhone: isNonCheckout,
            requestShipping: isNonCheckout,
        };
    },
);
